import Phaser from "phaser";
import { isoBox } from "./props";

/**
 * Edificios de los distritos de la ciudad (hub). Cada uno con SILUETA y tema
 * propios (obra, fábrica, laboratorio, torre, hospital, bomberos, central,
 * distrito gris, hotel, polígono), dibujados en isométrico con ventanas reales
 * sobre las caras. Origen (0,0) = base-centro, listo para colocar con depth.
 */

function darken(hex: number, f: number): number {
  const r = Math.max(0, Math.min(255, Math.round(((hex >> 16) & 255) * f)));
  const g = Math.max(0, Math.min(255, Math.round(((hex >> 8) & 255) * f)));
  const b = Math.max(0, Math.min(255, Math.round((hex & 255) * f)));
  return (r << 16) | (g << 8) | b;
}
const lite = (hex: number, f: number) => Math.min(0xffffff, darken(hex, f)) & 0xffffff;

// --- puntos sobre las caras visibles del isoBox (u: fondo→frente, v: arriba→abajo) ---
function faceL(hw: number, hd: number, ph: number, u: number, v: number): [number, number] {
  const tx = -hw + hw * u, ty = -ph + hd * u, by = hd * u;
  return [tx, ty + (by - ty) * v];
}
function faceR(hw: number, hd: number, ph: number, u: number, v: number): [number, number] {
  const tx = hw - hw * u, ty = -ph + hd * u, by = hd * u;
  return [tx, ty + (by - ty) * v];
}
function quad(g: Phaser.GameObjects.Graphics, pts: [number, number][], c: number): void {
  g.fillStyle(c, 1);
  g.beginPath(); g.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
  g.closePath(); g.fillPath();
}
/** Ventana (parallelogramo) sobre una cara. side: "L"|"R". */
function win(g: Phaser.GameObjects.Graphics, side: "L" | "R", hw: number, hd: number, ph: number, u: number, v: number, du: number, dv: number, c: number): void {
  const f = side === "L" ? faceL : faceR;
  quad(g, [f(hw, hd, ph, u - du, v - dv), f(hw, hd, ph, u + du, v - dv), f(hw, hd, ph, u + du, v + dv), f(hw, hd, ph, u - du, v + dv)], c);
}
/** Rejilla de ventanas en ambas caras. */
function windows(g: Phaser.GameObjects.Graphics, hw: number, hd: number, ph: number, rows: number, cols: number, on: number, off: number, seed = 1): void {
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    const u = (c + 0.5) / cols, v = 0.14 + (r + 0.5) / rows * 0.72;
    const lit = ((r * 7 + c * 3 + seed) % 4 === 0) ? on : off;
    win(g, "L", hw, hd, ph, u, v, 0.28 / cols * 1.4, 0.36 / rows * 1.4, lit);
    win(g, "R", hw, hd, ph, u, v, 0.28 / cols * 1.4, 0.36 / rows * 1.4, darken(lit, 0.88));
  }
}

function shadow(g: Phaser.GameObjects.Graphics, hw: number): void {
  g.fillStyle(0x000000, 0.16); g.fillEllipse(6, 10, hw * 2 + 22, 26);
}

/** Devuelve el edificio del distrito `id` (color = acento del distrito). */
export function cityBuilding(scene: Phaser.Scene, id: string, color: number): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  shadow(g, 30);
  switch (id) {
    case "obras": obras(g, color); break;
    case "fabrica": fabrica(g, color); break;
    case "labo": labo(g, color); break;
    case "torre": torre(g, color); break;
    case "poligono": poligono(g, color); break;
    case "bomberos": bomberos(g, color); break;
    case "hospital": hospital(g, color); break;
    case "central": central(g, color); break;
    case "gris": gris(g, color); break;
    case "hotel": hotel(g, color); break;
    default: generic(g, color); break;
  }
  return scene.add.container(0, 0, [g]);
}

// pequeña puerta en la cara izquierda, centrada abajo
function door(g: Phaser.GameObjects.Graphics, hw: number, hd: number, ph: number, c = 0x2b2620): void {
  win(g, "L", hw, hd, ph, 0.5, 0.86, 0.1, 0.12, c);
}

// ---------------- MOBILIARIO URBANO ----------------
function wrap(scene: Phaser.Scene, draw: (g: Phaser.GameObjects.Graphics) => void): Phaser.GameObjects.Container {
  const g = scene.add.graphics(); draw(g); return scene.add.container(0, 0, [g]);
}

/** Árbol de calle/parque. */
export function tree(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 4, 34, 14);
    g.fillStyle(0x6a4f2c, 1); g.fillRect(-4, -24, 8, 26);
    g.fillStyle(0x5a3f22, 1); g.fillRect(1, -24, 3, 26);
    const blob = (x: number, y: number, r: number, c: number) => { g.fillStyle(c, 1); g.fillCircle(x, y, r); };
    blob(-12, -36, 14, 0x357033); blob(12, -36, 14, 0x30692e); blob(0, -44, 19, 0x3f7d3a); blob(0, -32, 16, 0x4a8a42);
    g.fillStyle(0x5aa050, 0.55); g.fillCircle(-6, -49, 10); // luz arriba-izq
  });
}

/** Banco de parque. */
export function bench(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.14); g.fillEllipse(0, 3, 40, 12);
    g.fillStyle(0x4a4f57, 1); g.fillRect(-17, -8, 4, 8); g.fillRect(13, -8, 4, 8);
    // asiento iso
    g.fillStyle(0xa9803f, 1); g.beginPath(); g.moveTo(-20, -8); g.lineTo(2, -16); g.lineTo(22, -8); g.lineTo(0, 0); g.closePath(); g.fillPath();
    g.lineStyle(1, 0x6a4f2c, 0.7); g.beginPath(); g.moveTo(-14, -9); g.lineTo(8, -1); g.moveTo(-8, -12); g.lineTo(14, -4); g.strokePath();
    // respaldo
    g.fillStyle(0xb98a4a, 1); g.beginPath(); g.moveTo(-20, -8); g.lineTo(2, -16); g.lineTo(2, -26); g.lineTo(-20, -18); g.closePath(); g.fillPath();
  });
}

/** Farola. */
export function streetlamp(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.14); g.fillEllipse(0, 3, 16, 7);
    g.fillStyle(0x4a4f57, 1); g.fillRect(-2.5, -58, 5, 58);
    g.fillStyle(0x5f676d, 1); g.fillRect(-2.5, -58, 2, 58);
    g.lineStyle(3, 0x4a4f57, 1); g.beginPath(); g.moveTo(0, -58); g.lineTo(11, -62); g.strokePath();
    g.fillStyle(0xffe8a0, 0.18); g.fillCircle(12, -58, 15); // resplandor
    g.fillStyle(0x2b2f35, 1); g.fillRect(6, -63, 12, 5);
    g.fillStyle(0xffe8a0, 0.95); g.fillEllipse(12, -58, 10, 4);
  });
}

/** Coche/furgoneta (morro hacia SE, +x). */
export function car(scene: Phaser.Scene, color = 0xb23a2a): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    const dk = darken(color, 0.68), lt = lite(color, 1.1);
    g.fillStyle(0x000000, 0.2); g.fillEllipse(1, 7, 76, 18);
    // --- carrocería (perfil: maletero, luneta, techo, parabrisas, capó, morro) ---
    g.fillStyle(color, 1);
    g.beginPath();
    g.moveTo(-33, -7); g.lineTo(-31, -15); g.lineTo(-17, -19); g.lineTo(-9, -30);
    g.lineTo(9, -30); g.lineTo(18, -18); g.lineTo(31, -14); g.lineTo(34, -7);
    g.lineTo(31, -2); g.lineTo(-31, -2); g.closePath(); g.fillPath();
    // banda inferior más oscura
    g.fillStyle(dk, 1); g.beginPath(); g.moveTo(-31, -2); g.lineTo(31, -2); g.lineTo(30, -6); g.lineTo(-30, -6); g.closePath(); g.fillPath();
    // techo (más claro)
    g.fillStyle(lt, 1); g.beginPath(); g.moveTo(-9, -30); g.lineTo(9, -30); g.lineTo(16, -21); g.lineTo(-16, -21); g.closePath(); g.fillPath();
    // --- lunas ---
    g.fillStyle(0x8fc9d4, 0.95);
    g.beginPath(); g.moveTo(8, -29); g.lineTo(16, -20.5); g.lineTo(10, -20.5); g.lineTo(5, -29); g.closePath(); g.fillPath(); // parabrisas
    g.beginPath(); g.moveTo(-6, -29); g.lineTo(3, -29); g.lineTo(2, -21); g.lineTo(-8, -21); g.closePath(); g.fillPath(); // ventanilla
    g.beginPath(); g.moveTo(-8, -29); g.lineTo(-15, -20.5); g.lineTo(-11, -20.5); g.lineTo(-7, -29); g.closePath(); g.fillPath(); // luneta
    g.lineStyle(1.4, dk, 1); g.beginPath(); g.moveTo(-6, -29); g.lineTo(-8, -21); g.strokePath(); // montante
    g.fillStyle(0xffffff, 0.35); g.beginPath(); g.moveTo(-6, -29); g.lineTo(3, -29); g.lineTo(1, -26); g.lineTo(-6, -26); g.closePath(); g.fillPath(); // reflejo luna
    // línea de puerta y manilla
    g.lineStyle(1, dk, 0.7); g.beginPath(); g.moveTo(-1, -20); g.lineTo(-1, -6); g.strokePath();
    g.fillStyle(dk, 1); g.fillRect(-9, -14, 4, 1.6);
    // --- ruedas con arco ---
    g.fillStyle(dk, 1); g.fillEllipse(-18, -2, 20, 11); g.fillEllipse(20, -2, 20, 11);
    const wheel = (wx: number): void => { g.fillStyle(0x14141a, 1); g.fillCircle(wx, 0, 7.5); g.fillStyle(0x3a3f47, 1); g.fillCircle(wx, 0, 3.6); g.fillStyle(0xaab0b8, 1); g.fillCircle(wx, 0, 1.5); };
    wheel(-18); wheel(20);
    // --- faros / pilotos / parachoques ---
    g.fillStyle(0xfff2c0, 1); g.fillEllipse(31, -10, 4, 3); // faro delantero
    g.fillStyle(0xe03a2a, 1); g.fillEllipse(-30, -12, 3.5, 3); // piloto trasero
    g.fillStyle(0x2b2f35, 1); g.fillRect(29, -6, 6, 3.5); g.fillRect(-34, -7, 5, 3.5); // parachoques
    // brillo del techo
    g.fillStyle(0xffffff, 0.16); g.beginPath(); g.moveTo(-8, -29.5); g.lineTo(8, -29.5); g.lineTo(5, -27); g.lineTo(-6, -27); g.closePath(); g.fillPath();
  });
}

/** Paso de cebra sobre el asfalto. dir = orientación de la calle. */
export function crossing(scene: Phaser.Scene, dir: "x" | "y"): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    const HW = 64, HH = 32;
    const P = (du: number, dv: number): [number, number] => [HW * (du - dv), HH * (du + dv)];
    g.fillStyle(0xe8e4d8, 0.82);
    for (const s of [-0.42, -0.21, 0, 0.21, 0.42]) {
      let pts: [number, number][];
      if (dir === "x") pts = [P(s - 0.05, -0.4), P(s + 0.05, -0.4), P(s + 0.05, 0.4), P(s - 0.05, 0.4)];
      else pts = [P(-0.4, s - 0.05), P(0.4, s - 0.05), P(0.4, s + 0.05), P(-0.4, s + 0.05)];
      g.beginPath(); g.moveTo(pts[0][0], pts[0][1]); for (let i = 1; i < 4; i++) g.lineTo(pts[i][0], pts[i][1]); g.closePath(); g.fillPath();
    }
  });
}

/** Marquesina de parada de autobús. */
export function busstop(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.14); g.fillEllipse(-4, 4, 60, 16);
    // poste + señal de bus
    g.fillStyle(0x4a4f57, 1); g.fillRect(20, -54, 4, 54);
    g.fillStyle(0x2c6270, 1); g.fillCircle(22, -52, 9);
    g.fillStyle(0xffffff, 1); g.fillRect(17, -55, 10, 4); g.fillRect(18, -50, 8, 2);
    // marquesina: postes, techo iso, panel de cristal, banco
    g.fillStyle(0x6a6f77, 1); g.fillRect(-32, -42, 3, 42); g.fillRect(-2, -42, 3, 42);
    g.fillStyle(0x9aa0a8, 0.9); quad(g, [[-36, -42], [0, -42], [-8, -48], [-44, -48]], 0x9aa0a8);
    g.fillStyle(0x9cc0cc, 0.35); g.fillRect(-31, -38, 27, 26);
    g.fillStyle(0x8a744a, 1); g.fillRect(-30, -12, 26, 4);
  });
}

/** Fuente de plaza/parque. */
export function fountain(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 7, 72, 26);
    g.fillStyle(0xb2ac9c, 1); g.fillEllipse(0, 0, 70, 32);
    g.fillStyle(0x9a9488, 1); g.fillEllipse(0, 2, 70, 30);
    g.fillStyle(0x6f9fb2, 1); g.fillEllipse(0, -2, 56, 24); // agua
    g.fillStyle(0x9cc0cc, 0.6); g.fillEllipse(-8, -6, 26, 10);
    g.fillStyle(0xc6c1b3, 1); g.fillEllipse(0, -8, 20, 10); g.fillRect(-4, -26, 8, 18);
    g.fillStyle(0xbfe0e6, 0.7); g.fillEllipse(0, -28, 10, 5);
    g.lineStyle(2, 0xbfe0e6, 0.6); g.beginPath(); g.moveTo(0, -28); g.lineTo(-6, -38); g.moveTo(0, -28); g.lineTo(6, -38); g.strokePath();
  });
}

/** Casa de las afueras (tejado a dos aguas, chimenea, puerta y ventanas). */
export function house(scene: Phaser.Scene, color = 0xd8b98a, roof = 0xa85a44): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    shadow(g, 22);
    const HW = 26, HD = 16, PH = 30;
    isoBox(g, HW, HD, PH, color, { stroke: darken(color, 0.62) });
    const ry = -PH;
    quad(g, [[0, ry - 16], [HW + 5, ry + 2], [0, ry + 20], [-HW - 5, ry + 2]], roof); // tejado (rombo)
    quad(g, [[0, ry - 16], [HW + 5, ry + 2], [0, ry], [-HW - 5, ry + 2]], lite(roof, 1.12)); // agua iluminada
    g.lineStyle(2, darken(roof, 0.7), 0.9); g.beginPath(); g.moveTo(-HW - 5, ry + 2); g.lineTo(HW + 5, ry + 2); g.strokePath();
    g.fillStyle(darken(color, 0.7), 1); g.fillRect(HW * 0.42 - 3, ry - 13, 7, 15); // chimenea
    g.fillStyle(0x2b2620, 1); g.fillRect(HW * 0.42 - 3, ry - 15, 7, 3);
    door(g, HW, HD, PH, 0x5a3f22);
    win(g, "L", HW, HD, PH, 0.26, 0.42, 0.1, 0.13, 0xbfe0e6);
    win(g, "R", HW, HD, PH, 0.68, 0.42, 0.1, 0.13, darken(0xbfe0e6, 0.86));
  });
}

/** Vecino/a paseando (persona de calle, sin EPI). */
export function civilian(scene: Phaser.Scene, shirt = 0x3a6ea0, pants = 0x2b2f36, hair = 0x3a2a20, skin = 0xf0c9a0): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 3, 20, 8);
    g.fillStyle(pants, 1); g.fillRect(-6, -16, 5, 16); g.fillRect(2, -16, 5, 16);
    g.fillStyle(darken(pants, 0.7), 1); g.fillRect(-6, -3, 6, 3); g.fillRect(2, -3, 6, 3); // zapatos
    g.fillStyle(shirt, 1); g.fillRoundedRect(-8, -34, 16, 20, 3);
    g.fillStyle(shirt, 1); g.fillRect(-10, -33, 3, 15); g.fillRect(7, -33, 3, 15);
    g.fillStyle(skin, 1); g.fillCircle(0, -40, 6);
    g.fillStyle(hair, 1); g.fillEllipse(0, -44, 12, 7);
  });
}

/** Marichei: guía de orientación de Mi Ruta (abrigo coral, bolso y brújula). */
export function marichei(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.2); g.fillEllipse(0, 4, 24, 9);
    g.fillStyle(0x2b2f36, 1); g.fillRect(-6, -16, 5, 16); g.fillRect(2, -16, 5, 16);
    g.fillStyle(0x1a1a1f, 1); g.fillRect(-6, -3, 6, 3); g.fillRect(2, -3, 6, 3);
    g.fillStyle(0xdf5f2a, 1); g.fillRoundedRect(-9, -38, 18, 24, 4); // abrigo coral
    g.fillStyle(0x1f3b57, 1); g.fillRect(-8, -38, 16, 4); // bufanda navy
    g.fillStyle(0xdf5f2a, 1); g.fillRect(-11, -37, 3, 17); g.fillRect(8, -37, 3, 17);
    g.fillStyle(0xf0c9a0, 1); g.fillCircle(0, -44, 6.5);
    g.fillStyle(0x3a2a20, 1); g.fillEllipse(0, -48, 14, 8); g.fillCircle(7, -50, 4); // pelo recogido/tocado
    g.fillStyle(0x8a5a2a, 1); g.fillRect(-15, -28, 8, 10); g.lineStyle(2, 0x6a4520, 1); g.strokeRect(-15, -28, 8, 10); // bolso bandolera
    // brújula de Mi Ruta
    g.fillStyle(0xf0a91b, 1); g.fillCircle(12, -24, 4.5);
    g.fillStyle(0xffffff, 1); g.fillCircle(12, -24, 2.8);
    g.fillStyle(0xdf5f2a, 1); g.fillTriangle(12, -27, 10.5, -23, 13.5, -23);
  });
}

/** Aficionado del Sevilla (camiseta blanca con detalles rojos). */
export function sevillista(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 3, 20, 8);
    g.fillStyle(0xc4122e, 1); g.fillRect(-6, -18, 5, 7); g.fillRect(2, -18, 5, 7); // pantalón rojo
    g.fillStyle(0x2b2f36, 1); g.fillRect(-6, -11, 5, 11); g.fillRect(2, -11, 5, 11); // piernas
    g.fillStyle(0x1a1a1f, 1); g.fillRect(-6, -3, 6, 3); g.fillRect(2, -3, 6, 3);
    g.fillStyle(0xf4f4f4, 1); g.fillRoundedRect(-8, -34, 16, 20, 3); // camiseta blanca
    g.fillStyle(0xc4122e, 1); g.fillRect(-8, -34, 16, 2.5); // franja roja del cuello
    g.fillStyle(0xf4f4f4, 1); g.fillRect(-10, -33, 3, 15); g.fillRect(7, -33, 3, 15); // brazos blancos
    g.fillStyle(0xc4122e, 1); g.fillRect(-10, -33, 3, 6); g.fillRect(7, -33, 3, 6); // mangas rojas
    g.fillStyle(0xc4122e, 1); g.fillRect(-2, -30, 4, 5); // escudito
    g.fillStyle(0xf0c9a0, 1); g.fillCircle(0, -40, 6);
    g.fillStyle(0x3a2a20, 1); g.fillEllipse(0, -44, 12, 7);
  });
}

/** Aficionado del Betis (camiseta a rayas verdiblancas). */
export function betico(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 3, 20, 8);
    g.fillStyle(0x0b7a3b, 1); g.fillRect(-6, -18, 5, 7); g.fillRect(2, -18, 5, 7); // pantalón verde
    g.fillStyle(0x2b2f36, 1); g.fillRect(-6, -11, 5, 11); g.fillRect(2, -11, 5, 11);
    g.fillStyle(0x1a1a1f, 1); g.fillRect(-6, -3, 6, 3); g.fillRect(2, -3, 6, 3);
    g.fillStyle(0xf4f4f4, 1); g.fillRoundedRect(-8, -34, 16, 20, 3); // base blanca
    g.fillStyle(0x0b7a3b, 1);
    for (const rx of [-8, -4, 0, 4]) g.fillRect(rx, -34, 2.6, 20); // rayas verdes
    g.fillStyle(0x0b7a3b, 1); g.fillRect(-10, -33, 3, 16); g.fillRect(7, -33, 3, 16); // mangas verdes
    g.fillStyle(0xf0c9a0, 1); g.fillCircle(0, -40, 6);
    g.fillStyle(0x2a2018, 1); g.fillEllipse(0, -44, 12, 7);
  });
}

/** Pájaro (silueta de alas, para el cielo). */
export function bird(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  g.lineStyle(2, 0x3a3f47, 0.85);
  g.beginPath(); g.moveTo(-9, 0); g.lineTo(-3, -4); g.lineTo(0, -1.5); g.lineTo(3, -4); g.lineTo(9, 0); g.strokePath();
  return scene.add.container(0, 0, [g]);
}

/** Perrete paseando (vista lateral). */
export function dog(scene: Phaser.Scene, color = 0x8a6a45): Phaser.GameObjects.Container {
  return wrap(scene, (g) => {
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 2, 22, 7);
    g.fillStyle(color, 1);
    g.fillRoundedRect(-10, -11, 17, 8, 3); // cuerpo
    g.fillRect(-9, -4, 3, 5); g.fillRect(-5, -4, 3, 5); g.fillRect(2, -4, 3, 5); g.fillRect(6, -4, 3, 5); // patas
    g.fillCircle(9, -12, 4.5); // cabeza
    g.fillStyle(darken(color, 0.78), 1); g.fillTriangle(7, -16, 11, -16, 9, -12); // oreja
    g.fillStyle(0x2a2018, 1); g.fillCircle(12.5, -12, 1.2); // hocico
    g.lineStyle(2.5, color, 1); g.beginPath(); g.moveTo(-10, -10); g.lineTo(-15, -15); g.strokePath(); // rabo
  });
}

function generic(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 28, 18, 50, 0xcfc7b6, { stroke: 0x9a927f });
  windows(g, 28, 18, 50, 3, 3, 0xbfe0e6, 0x6b7f86);
  quad(g, [faceL(28, 18, 50, 0.1, -0.02), faceL(28, 18, 50, 0.9, -0.02), faceL(28, 18, 50, 0.9, 0.06), faceL(28, 18, 50, 0.1, 0.06)], color);
  door(g, 28, 18, 50);
}

// --- OBRAS: edificio en construcción (hormigón + forjados + grúa) ---
function obras(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 28, 18, 44, 0xc6c0b2, { stroke: 0x8f8878 });
  // forjados/plantas insinuados
  g.lineStyle(1.4, 0x9a927f, 0.6);
  for (const v of [0.35, 0.6, 0.85]) { const a = faceL(28, 18, 44, 0.06, v), b = faceL(28, 18, 44, 0.94, v); g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.strokePath(); }
  // pilares que sobresalen (obra sin terminar)
  g.lineStyle(4, 0xbfb7a6, 1);
  for (const u of [0.15, 0.5, 0.85]) { const t = faceL(28, 18, 44, u, 0); g.beginPath(); g.moveTo(t[0], t[1]); g.lineTo(t[0], t[1] - 14); g.strokePath(); }
  // varillas
  g.lineStyle(1.5, 0x6a6f77, 1);
  for (const u of [0.3, 0.7]) { const t = faceR(28, 18, 44, u, 0); g.beginPath(); g.moveTo(t[0], t[1]); g.lineTo(t[0] + 1, t[1] - 16); g.strokePath(); }
  // grúa
  g.lineStyle(3, 0xe08a2a, 1);
  g.beginPath(); g.moveTo(24, -44); g.lineTo(24, -96); g.moveTo(24, -92); g.lineTo(64, -96); g.moveTo(24, -92); g.lineTo(6, -88); g.strokePath();
  g.fillStyle(0xe08a2a, 1); g.fillRect(20, -96, 8, 8);
  g.lineStyle(1, 0x2b2620, 0.8); g.beginPath(); g.moveTo(52, -95); g.lineTo(52, -80); g.strokePath();
  // banda de obra + puerta
  quad(g, [faceL(28, 18, 44, 0.06, -0.03), faceL(28, 18, 44, 0.94, -0.03), faceL(28, 18, 44, 0.94, 0.04), faceL(28, 18, 44, 0.06, 0.04)], color);
  door(g, 28, 18, 44, 0x8a5a2a);
}

// --- FÁBRICA: nave con dientes de sierra y chimenea ---
function fabrica(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 32, 20, 40, 0x9a8f7c, { stroke: 0x6e6555 });
  windows(g, 32, 20, 40, 2, 4, 0xbfe0e6, 0x5b6a5f, 3);
  // techo en diente de sierra
  g.fillStyle(0x7d7466, 1);
  for (let i = 0; i < 3; i++) { const x = -20 + i * 20; g.fillTriangle(x, -40, x + 12, -52, x + 20, -40); }
  g.fillStyle(0x8ac6d0, 0.5);
  for (let i = 0; i < 3; i++) { const x = -20 + i * 20; g.fillTriangle(x + 12, -52, x + 20, -40, x + 12, -42); }
  // chimenea + humo
  g.fillStyle(0x8a5a3a, 1); g.fillRect(20, -78, 9, 40);
  g.fillStyle(0xb23a2a, 1); g.fillRect(20, -78, 9, 5);
  g.fillStyle(0xcfc7b6, 0.5); g.fillCircle(26, -84, 7); g.fillCircle(32, -92, 9); g.fillCircle(40, -100, 11);
  door(g, 32, 20, 40, color);
}

// --- LABORATORIO: bloque limpio + cristal verde + matraz ---
function labo(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 28, 18, 52, 0xe6e3da, { stroke: 0xb7b3a6 });
  // franja de cristal verde
  for (let c = 0; c < 4; c++) { const u = (c + 0.5) / 4; win(g, "L", 28, 18, 52, u, 0.4, 0.09, 0.14, color); win(g, "R", 28, 18, 52, u, 0.4, 0.09, 0.14, darken(color, 0.85)); }
  windows(g, 28, 18, 52, 2, 3, 0xbfe0e6, 0x9fb8b2, 2);
  // matraz en el tejado
  g.fillStyle(0x1c6b64, 1); g.fillTriangle(2, -52, -6, -66, 10, -66); g.fillRect(0, -70, 4, 6);
  g.fillStyle(0x5fd0c4, 0.8); g.fillTriangle(2, -54, -3, -62, 7, -62);
  door(g, 28, 18, 52, 0x2c3f52);
}

// --- TORRE: rascacielos de oficinas ---
function torre(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 22, 14, 84, 0x3f5a82, { stroke: 0x27374f });
  windows(g, 22, 14, 84, 6, 3, 0xbfe0e6, 0x2f4763, 5);
  // remate + antena
  isoBox(g, 22, 14, 6, lite(color, 1.1), { stroke: 0x27374f, oy: -84 });
  g.lineStyle(2, 0xc9c9cf, 1); g.beginPath(); g.moveTo(0, -90); g.lineTo(0, -106); g.strokePath();
  g.fillStyle(0xb23a2a, 1); g.fillCircle(0, -106, 2.5);
  door(g, 22, 14, 84, 0x1b2838);
}

// --- POLÍGONO: nave-almacén ancha con portón ---
function poligono(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 36, 22, 34, 0xb9ad92, { stroke: 0x8a7f68 });
  // chapa acanalada en la cara izquierda
  g.lineStyle(1, 0x8a7f68, 0.5);
  for (let c = 1; c < 10; c++) { const u = c / 10; const t = faceL(36, 22, 34, u, 0.05), b = faceL(36, 22, 34, u, 0.95); g.beginPath(); g.moveTo(t[0], t[1]); g.lineTo(b[0], b[1]); g.strokePath(); }
  // portón (roller door)
  win(g, "L", 36, 22, 34, 0.5, 0.7, 0.16, 0.24, 0x5a5f68);
  win(g, "L", 36, 22, 34, 0.5, 0.7, 0.14, 0.20, color);
  // techo claro
  quad(g, [faceR(36, 22, 34, 0.05, -0.02), faceR(36, 22, 34, 0.95, -0.02), faceR(36, 22, 34, 0.95, 0.06), faceR(36, 22, 34, 0.05, 0.06)], lite(0xb9ad92, 1.08));
}

// --- BOMBEROS: parque rojo con portones y torre ---
function bomberos(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 30, 18, 42, 0xb0402f, { stroke: 0x7a2418 });
  // dos portones de garaje
  win(g, "L", 30, 18, 42, 0.32, 0.72, 0.13, 0.2, 0xf0d8a0);
  win(g, "L", 30, 18, 42, 0.68, 0.72, 0.13, 0.2, 0xf0d8a0);
  // banda blanca
  for (const v of [0.42]) { const a = faceL(30, 18, 42, 0.05, v), b = faceL(30, 18, 42, 0.95, v); g.lineStyle(3, 0xf4ead0, 0.9); g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.strokePath(); }
  // torre de mangueras
  isoBox(g, 8, 6, 60, 0x9a3626, { stroke: 0x6a1f14, ox: 22, oy: 0 });
  g.fillStyle(0x2b2620, 1); g.fillRect(18, -58, 8, 8);
  door(g, 30, 18, 42, darken(color, 0.45));
}

// --- HOSPITAL: bloque blanco con cruz roja ---
function hospital(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 30, 18, 54, 0xeff0ec, { stroke: 0xc2c3bd });
  windows(g, 30, 18, 54, 4, 4, 0xbfe0e6, 0xcfe0e6, 4);
  // cruz roja en el remate
  g.fillStyle(0xffffff, 1); isoBox(g, 30, 18, 8, 0xffffff, { stroke: 0xc2c3bd, oy: -54 });
  g.fillStyle(0xd23b2a, 1); g.fillRect(-6, -74, 12, 4); g.fillRect(-2, -78, 4, 12);
  // marquesina de urgencias
  g.fillStyle(color, 1); quad(g, [faceL(30, 18, 54, 0.3, 0.62), faceL(30, 18, 54, 0.75, 0.62), faceL(30, 18, 54, 0.75, 0.66), faceL(30, 18, 54, 0.3, 0.66)], color);
  door(g, 30, 18, 54, 0x4a90a4);
}

// --- CENTRAL ELÉCTRICA: subestación con transformadores y rayo ---
function central(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 30, 20, 30, 0xbdb39a, { stroke: 0x8a8068 });
  // transformadores (cilindros)
  for (const ox of [-14, 4]) { g.fillStyle(0x8a9299, 1); g.fillRect(ox, -50, 12, 24); g.fillStyle(0x6a737a, 1); g.fillEllipse(ox + 6, -50, 12, 6); g.fillStyle(0xa9b0b6, 1); g.fillEllipse(ox + 6, -26, 12, 6); }
  // aisladores
  g.fillStyle(0xc9a20a, 1); g.fillCircle(-8, -52, 2); g.fillCircle(10, -52, 2);
  // pilón + cables
  g.lineStyle(2, 0x6a6f77, 1); g.beginPath(); g.moveTo(24, -30); g.lineTo(24, -70); g.moveTo(16, -64); g.lineTo(32, -64); g.strokePath();
  g.lineStyle(1, 0x2b2620, 0.7); g.beginPath(); g.moveTo(16, -64); g.lineTo(-14, -50); g.moveTo(32, -64); g.lineTo(10, -50); g.strokePath();
  // señal de rayo
  g.fillStyle(color, 1); g.fillTriangle(-24, -34, -16, -34, -20, -46); g.fillStyle(0x2b2620, 1); g.fillTriangle(-21, -44, -19, -38, -22, -40);
  door(g, 30, 20, 30, 0x6a1f14);
}

// --- DISTRITO GRIS: oficina apagada (psicosocial) ---
function gris(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 28, 18, 56, 0x8a8a90, { stroke: 0x5f5f66 });
  windows(g, 28, 18, 56, 5, 3, 0x9aa0a8, 0x6a6a72, 6); // ventanas apagadas
  // nubarrón encima
  g.fillStyle(0x6a6a72, 0.7); g.fillEllipse(0, -60, 40, 14); g.fillEllipse(-14, -62, 20, 12); g.fillEllipse(14, -60, 22, 12);
  g.lineStyle(1.5, 0x8a9098, 0.5); for (const x of [-10, 2, 12]) { g.beginPath(); g.moveTo(x, -54); g.lineTo(x - 3, -48); g.strokePath(); } // lluvia
  door(g, 28, 18, 56, darken(color, 0.6));
}

// --- HOTEL: fachada con balcones y rótulo ---
function hotel(g: Phaser.GameObjects.Graphics, color: number) {
  isoBox(g, 28, 18, 62, 0xc9b48a, { stroke: 0x9a875f });
  windows(g, 28, 18, 62, 5, 3, 0xffe8b8, 0x9a875f, 7);
  // balcones (ledges) en la cara izquierda
  g.lineStyle(2, 0x8a7a55, 0.8);
  for (const v of [0.32, 0.5, 0.68]) { const a = faceL(28, 18, 62, 0.1, v), b = faceL(28, 18, 62, 0.9, v); g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.strokePath(); }
  // marquesina + rótulo
  g.fillStyle(color, 1); g.fillRect(-20, -20, 40, 5);
  g.fillStyle(0xf4ead0, 1); g.fillRect(-14, -70, 28, 8);
  g.fillStyle(color, 1); g.fillRect(-11, -68, 4, 4); g.fillRect(-4, -68, 4, 4); g.fillRect(3, -68, 4, 4);
  door(g, 28, 18, 62, 0x5a3a24);
}
